
import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchHqPerformance,
  fetchTmPerformance,
  fetchVolatility,
  fetchCreditNotes,
  fetchBreakdownTable,
} from "./api/sfeApi";
import useSfeFilters from "./hooks/useSfeFilters";
import { formatErrorMessage, normalizeApiError } from "../../utils/apiErrors";
import "./SfeDashboard.css";

// ── panel error state ─────────────────────────────────────────────────────────
// One slot per data panel. Before this, every fetcher swallowed its failure and
// returned a default shape, so a `success: false` envelope reached the user as an
// empty table with nothing to explain it (Req 9.6). Each slot is cleared when that
// panel starts a request and filled when the request rejects.
const NO_PANEL_ERRORS = Object.freeze({
  performance: null,
  breakdown: null,
  volatility: null,
  creditNotes: null,
});

/**
 * True for a rejection caused by cancellation.
 *
 * Same classifier as `hooks/useSfeFilters.js` and `utils/axiosConfig.jsx`: an
 * intentional cancellation is not a failure and must never reach a panel banner.
 */
function isAbortError(error) {
  if (error === null || typeof error !== "object") return false;
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED"
  );
}

/** Trimmed non-empty string (or finite number as string), else null. */
function readReference(value) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Turns a rejection into what a panel banner renders.
 *
 * `SfeApiError` and every rejection from the shared Axios pipeline carry an
 * `apiError` descriptor; `normalizeApiError` covers anything thrown outside both.
 * `formatErrorMessage` is called with the reference withheld, so it still resolves
 * the sanitized message (and the generic per-kind fallback when the backend sent
 * none) while the reference is returned separately — the banner emphasises it in a
 * `<strong>` rather than printing the `(Reference: …)` suffix a second time
 * (Req 10.1, 10.3).
 */
function describePanelError(error) {
  const descriptor =
    error !== null && typeof error === "object" && error.apiError
      ? error.apiError
      : normalizeApiError(error);

  return {
    message: formatErrorMessage({ ...descriptor, referenceId: null }),
    referenceId: readReference(descriptor.referenceId),
  };
}

// ── Panel-level error banner ──────────────────────────────────────────────────
// Announced with `role="alert"`, and marked by a glyph as well as colour so the
// failure is not conveyed by colour alone.
function PanelError({ error, testId }) {
  if (!error) return null;
  return (
    <div className="sfe-panel-error" role="alert" data-testid={testId}>
      <span className="sfe-panel-error__icon" aria-hidden="true">⚠</span>
      <span className="sfe-panel-error__text">
        {error.message}
        {error.referenceId && (
          <>
            {" "}
            <strong>Reference: {error.referenceId}</strong>
          </>
        )}
      </span>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────
function fmtValue(v) {
  if (v === null || v === undefined || !isFinite(v)) return "—";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
  if (abs >= 100000)   return `${sign}₹${(abs / 100000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
  if (abs >= 1000)     return `${sign}₹${(abs / 1000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} K`;
  return `${sign}₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function fmtPct(v, signed = false) {
  if (v === null || v === undefined || !isFinite(v)) return "—";
  return `${signed && v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`;
}

function clsAch(v) {
  if (!isFinite(v)) return "";
  if (v >= 100) return "sfe-good";
  if (v >= 95)  return "sfe-blue";
  if (v >= 90)  return "sfe-warn";
  return "sfe-bad";
}

function clsGrowth(v) {
  if (!isFinite(v) || Math.abs(v) < 0.01) return "";
  return v >= 0 ? "sfe-good" : "sfe-bad";
}

// ── Multi-select dropdown ──────────────────────────────────────────────────────
// `error` / `onRetry` come from `useSfeFilters`: a level whose request failed used to
// render as an ordinary empty dropdown, indistinguishable from a level the backend
// legitimately has no values for. The message is announced (`role="alert"`) and the
// retry re-requests just that level.
function MultiSelect({ label, options, selected, onChange, disabled, loading, error, onRetry }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const preview = selected.length === 0
    ? "All"
    : selected.length === 1
      ? (options.find(o => o.id === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  function toggle(id) {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  }

  const errorId = error ? `sfe-filter-error-${label.toLowerCase()}` : undefined;

  return (
    <div className={`sfe-filter ${disabled ? "sfe-filter--disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className={`sfe-filter__btn ${selected.length > 0 ? "is-active" : ""}`}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        aria-describedby={errorId}
      >
        <span>
          {disabled && <span className="filter-lock">🔒</span>}
          {label}
          {selected.length > 0 && <span className="filter-badge">{selected.length}</span>}
        </span>
        {loading ? <span className="filter-spin" /> : <span>▼</span>}
      </button>

      {error && (
        <div className="sfe-filter__error" id={errorId} role="alert">
          <span>{error}</span>
          {onRetry && <button type="button" onClick={onRetry}>Retry {label}</button>}
        </div>
      )}

      {open && (
        <div className="sfe-filter__menu">
          <div className="sfe-filter__actions">
            <button type="button" onClick={() => onChange(options.map(o => o.id))}>All</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="sfe-filter__options">
            {options.length === 0 && <div className="sfe-filter__empty">No options</div>}
            {options.map(opt => (
              <label key={opt.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(opt.id)}
                  onChange={() => toggle(opt.id)}
                />
                <span className="sfe-filter__opt-label">{opt.label}</span>
                {opt.code && <span className="sfe-filter__opt-code">{opt.code}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Drill-down modal ──────────────────────────────────────────────────────────
function DrillModal({ title, data, onClose, mode = "HQ" }) {
  const isTm = mode === "TM";
  const countLabel = isTm
    ? `${data.length} TM${data.length !== 1 ? "s" : ""}`
    : `${data.length} HQ${data.length !== 1 ? "s" : ""}`;

  return (
    <div className="drill-modal-overlay" onClick={onClose}>
      <div className="drill-modal" onClick={e => e.stopPropagation()}>
        <div className="drill-modal-head">
          <h3>{title} — {countLabel}</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="drill-modal-body">
          {data.length === 0 ? (
            <div className="drill-empty">No {isTm ? "TMs" : "HQs"} in this band.</div>
          ) : isTm ? (
            <table className="sfe-table">
              <thead>
                <tr>
                  <th>#</th><th>TM NAME</th><th>EMP CODE</th><th>HQ</th>
                  <th>HQ Sales</th><th>HQ Target</th><th>Ach %</th>
                  <th>Growth %</th><th>PCPM/TM</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t, i) => (
                  <tr key={(t.emp_code || "") + i}>
                    <td>{i + 1}</td>
                    <td>{t.tm_name}</td>
                    <td>{t.emp_code}</td>
                    <td>{t.hq_name}</td>
                    <td>{fmtValue(t.hq_sales)}</td>
                    <td>{t.hq_budget > 0 ? fmtValue(t.hq_budget) : "—"}</td>
                    <td className={clsAch(t.achievement)}>{fmtPct(t.achievement)}</td>
                    <td className={clsGrowth(t.growth)}>{fmtPct(t.growth, true)}</td>
                    <td>{t.pcpm_tm ? fmtValue(t.pcpm_tm) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="sfe-table">
              <thead>
                <tr>
                  <th>#</th><th>HQ</th><th>Zone</th><th>TMs</th>
                  <th>Target</th><th>Sales</th><th>Ach %</th>
                  <th>Growth %</th><th>PCPM/TM</th>
                </tr>
              </thead>
              <tbody>
                {data.map((h, i) => (
                  <tr key={h.hq_name + i}>
                    <td>{i + 1}</td>
                    <td>{h.hq_name}</td>
                    <td>{h.zone || "—"}</td>
                    <td>{h.tms}</td>
                    <td>{h.budget > 0 ? fmtValue(h.budget) : "\u2014"}</td>
                    <td>{fmtValue(h.sales)}</td>
                    <td className={clsAch(h.achievement)}>{fmtPct(h.achievement)}</td>
                    <td className={clsGrowth(h.growth)}>{fmtPct(h.growth, true)}</td>
                    <td>{h.pcpm_tm ? fmtValue(h.pcpm_tm) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ title, value, subtitle, children, toggle }) {
  return (
    <div className="sfe-kpi">
      <div className="sfe-kpi__top">
        <div className="sfe-kpi__label">{title}</div>
        {toggle && <div className="sfe-kpi__toggle">{toggle}</div>}
      </div>
      <div className="sfe-kpi__value">{value}</div>
      <div className="sfe-kpi__subtitle">{subtitle}</div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SfeDashboard() {
  // The whole cascade — selections, options, loading, per-level errors, and the
  // serialized `params` the data panels send — lives in `hooks/useSfeFilters.js`
  // (Req 8.1-8.9). It replaced seven `useEffect` steps here, each of which re-derived
  // its own parent parameters, cleared its own children, and cancelled nothing.
  const {
    selections, options, loading: filterLoading, errors: filterErrors,
    params: filterParams, paramsKey, canFetch, select, reset, refresh,
  } = useSfeFilters();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [hqData, setHqData] = useState(null);
  const [tmData, setTmData] = useState([]);
  const [drillModal, setDrillModal] = useState(null);
  const [growthBasis, setGrowthBasis] = useState("HQ");
  const [spreadBasis, setSpreadBasis] = useState("HQ");
  const [pcpmBasis, setPcpmBasis] = useState("HQ");
  const [breakdown, setBreakdown] = useState("Brand");
  const [breakdownRows, setBreakdownRows] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [volData, setVolData] = useState(null);
  const [volLoading, setVolLoading] = useState(false);
  const [cnData, setCnData] = useState(null);
  const [cnLoading, setCnLoading] = useState(false);
  const [cnType, setCnType] = useState("Expiry");

  // One error slot per data panel (Req 9.6, 10.1, 10.3). `setPanelError` is the only
  // writer, so a panel's previous failure is always replaced rather than merged.
  const [panelErrors, setPanelErrors] = useState(NO_PANEL_ERRORS);

  function setPanelError(panel, error) {
    setPanelErrors(prev => (prev[panel] === error ? prev : { ...prev, [panel]: error }));
  }

  /**
   * `catch` handler for one panel: aborts are swallowed, everything else becomes the
   * panel's banner. `panels` takes more than one name for a fetch whose data feeds
   * several panels.
   */
  function failPanels(...panels) {
    return error => {
      if (isAbortError(error)) return;
      const described = describePanelError(error);
      setPanelErrors(prev => {
        const next = { ...prev };
        panels.forEach(panel => { next[panel] = described; });
        return next;
      });
    };
  }

  const BREAKDOWNS = ["State", "BDM", "ZM", "RM", "HQ", "Brand"];

  // ── Main data fetch ────────────────────────────────────────────────────────
  // `filterParams` arrives from the hook already serialized under the backend's
  // parameter names (Req 8.8), so it spreads straight into the fetchers. `paramsKey`
  // is its stable identity — the dependency that used to be a `JSON.stringify` of a
  // locally rebuilt object.
  useEffect(() => {
    if (!canFetch) { setSummary(null); setHqData(null); setTmData([]); setVolData(null); setCnData(null); setBreakdownRows([]); setPanelErrors(NO_PANEL_ERRORS); return; }
    setSummary(null); setHqData(null); setTmData([]); setVolData(null); setCnData(null); setBreakdownRows([]);
    // Every panel is about to request: clear the previous round's banners so a stale
    // failure cannot outlive the filters that produced it.
    setPanelErrors(NO_PANEL_ERRORS);
    setLoading(true); setBreakdownLoading(true); setVolLoading(true); setCnLoading(true);

    const currentBreakdown = breakdown.toLowerCase();

    fetchHqPerformance(filterParams).then(d => {
      setHqData(d);
      if (d) {
        setSummary({ total_sales: d.total_sales || 0, total_budget: d.total_budget || 0, achievement: d.achievement || null, ly_sales: d.ly_sales || 0, growth: d.growth || null });
        if (breakdown.toLowerCase() === "hq" && d.hq_list) {
          setBreakdownRows(d.hq_list.map(h => ({ name: h.hq_name, total_sales: h.sales, total_budget: h.budget, achievement: h.achievement, ly_sales: h.ly_sales, growth: h.growth })));
          setBreakdownLoading(false);
        }
      }
      setLoading(false);
    }).catch(error => {
      setLoading(false);
      // When the breakdown is showing HQ rows it is fed by this same response, so the
      // failure belongs to both panels and the breakdown's spinner has to stop too.
      if (currentBreakdown === "hq") { setBreakdownLoading(false); failPanels("performance", "breakdown")(error); return; }
      failPanels("performance")(error);
    });

    if (currentBreakdown !== "hq") {
      fetchBreakdownTable({ ...filterParams, group_by: currentBreakdown }).then(setBreakdownRows).catch(failPanels("breakdown")).finally(() => setBreakdownLoading(false));
    }

    // Volatility and credit notes are requested with the rest of the panel data. They
    // used to be held back behind a 300 ms `setTimeout`, which only staggered the
    // requests — it cancelled nothing and guarded no correctness property, while
    // leaving both panels spinning for an extra 300 ms on every filter change.
    fetchVolatility(filterParams).then(setVolData).catch(failPanels("volatility")).finally(() => setVolLoading(false));
    fetchCreditNotes({ ...filterParams, cn_type: cnType }).then(setCnData).catch(failPanels("creditNotes")).finally(() => setCnLoading(false));

    // TM performance data for TM-mode drill-downs
    fetchTmPerformance(filterParams).then(setTmData).catch(() => setTmData([]));
    // `breakdown` and `cnType` are read but deliberately not depended on: their own
    // handlers (`changeBreakdown`, `changeCnType`) re-fetch just their panel, and
    // listing them here would refetch all four panels on a tab click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, canFetch]);

  function changeCnType(type) {
    setCnType(type);
    // The fetchers throw `SfeApiError` rather than returning an empty default
    // (Req 9.6), so the rejection is caught here and shown on the credit-notes panel.
    if (!canFetch) return;
    setPanelError("creditNotes", null);
    setCnLoading(true);
    fetchCreditNotes({ ...filterParams, cn_type: type }).then(setCnData).catch(failPanels("creditNotes")).finally(() => setCnLoading(false));
  }

  function changeBreakdown(dim) {
    setBreakdown(dim);
    if (!canFetch) return;
    const dimLower = dim.toLowerCase();
    if (dimLower === "hq" && hqData && hqData.hq_list) {
      // Served from the HQ response already in hand: no request, so no new failure —
      // but the previous dimension's banner would otherwise sit above valid rows.
      setPanelError("breakdown", panelErrors.performance);
      setBreakdownRows(hqData.hq_list.map(h => ({ name: h.hq_name, total_sales: h.sales, total_budget: h.budget, achievement: h.achievement, ly_sales: h.ly_sales, growth: h.growth })));
      return;
    }
    setPanelError("breakdown", null);
    setBreakdownLoading(true);
    fetchBreakdownTable({ ...filterParams, group_by: dimLower }).then(setBreakdownRows).catch(failPanels("breakdown")).finally(() => setBreakdownLoading(false));
  }

  function openDrill(title, filterFn, mode = "HQ") {
    if (mode === "TM") {
      const enriched = tmData.map(t => ({
        ...t,
        vs_div_pcpm: (divPcpmTm > 0 && t.pcpm_tm) ? (t.pcpm_tm / divPcpmTm) * 100 : null
      }));
      const tms = enriched.filter(filterFn);
      setDrillModal({ title, data: tms, mode });
    } else {
      const hqs = (hqData?.hq_list || []).filter(filterFn);
      setDrillModal({ title, data: hqs, mode });
    }
  }

  const selMonths = selections.months;
  const selYears = selections.fiscalYear;

  const periodLabel = useMemo(() => {
    if (selMonths.length === 0) return "—";
    if (selMonths.length === 1) { const mo = options.months.find(m => m.id === selMonths[0]); return mo ? mo.label.split(" ")[0].substring(0, 3).toUpperCase() : selMonths[0]; }
    if (selMonths.length === 12) return "YTD";
    return `${selMonths.length}M`;
  }, [selMonths, options.months]);

  const fyLabel = selYears.length === 1 ? selYears[0] : selYears.join(", ");
  const sp = hqData?.spread_buckets || {};
  const gp = hqData?.growth_buckets || {};
  const pp = hqData?.pcpm_buckets || {};
  const totalHqs = hqData?.total_hqs || 0;
  const totalTms = hqData?.total_tms || 0;
  const hqList = hqData?.hq_list || [];

  const tmSpread = useMemo(() => { const b = { good: 0, mid: 0, warn: 0, bad: 0 }; hqList.forEach(h => { if (h.achievement > 100) b.good += h.tms; else if (h.achievement >= 95) b.mid += h.tms; else if (h.achievement >= 90) b.warn += h.tms; else b.bad += h.tms; }); return b; }, [hqList]);
  const tmGrowth = useMemo(() => { const b = { high: 0, grow: 0, slight: 0, sharp: 0 }; hqList.forEach(h => { if (h.growth == null) return; if (h.growth > 10) b.high += h.tms; else if (h.growth >= 0) b.grow += h.tms; else if (h.growth >= -10) b.slight += h.tms; else b.sharp += h.tms; }); return b; }, [hqList]);
  const tmPcpm = useMemo(() => { const b = { high: 0, above: 0, low: 0, critical: 0 }; hqList.forEach(h => { if (h.vs_div_pcpm == null) return; if (h.vs_div_pcpm > 150) b.high += h.tms; else if (h.vs_div_pcpm >= 100) b.above += h.tms; else if (h.vs_div_pcpm >= 50) b.low += h.tms; else b.critical += h.tms; }); return b; }, [hqList]);

  const divPcpmTm = totalTms > 0 ? (summary?.total_sales || 0) / totalTms / Math.max(selMonths.length, 1) : 0;
  const divPcpmHq = totalHqs > 0 ? (summary?.total_sales || 0) / totalHqs / Math.max(selMonths.length, 1) : 0;

  return (
    <div className="sfe-page">
      <header className="sfe-header">
        <div className="sfe-header__left">
          <div className="sfe-header__title">Sales Performance Dashboard</div>
          <div className="sfe-header__subtitle">Sales Force Effectiveness</div>
        </div>
        <div className="sfe-header__right">
          <button className="sfe-pdf-btn" type="button" onClick={() => window.print()}>↧ PDF</button>
          <div className="sfe-brand">Emcure <span>Sales Force Effectiveness</span></div>
        </div>
      </header>

      <div className="sfe-filterbar">
        {[
          { level: "fiscalYear", label: "YEAR", parent: null },
          { level: "months", label: "PERIOD", parent: "fiscalYear" },
          { level: "divisions", label: "DIVISION", parent: "months" },
          { level: "states", label: "STATE", parent: "divisions" },
          { level: "brands", label: "BRAND", parent: "states" },
          { level: "bdm", label: "BDM", parent: "divisions" },
          { level: "zm", label: "ZM", parent: "divisions" },
        ].map(f => (
          <MultiSelect
            key={f.level}
            label={f.label}
            options={options[f.level]}
            selected={selections[f.level]}
            onChange={v => select(f.level, v)}
            disabled={f.parent !== null && selections[f.parent].length === 0}
            loading={filterLoading[f.level]}
            error={filterErrors[f.level]}
            onRetry={() => refresh(f.level)}
          />
        ))}
        <button className="sfe-reset" type="button" onClick={reset}>Reset Filters</button>
      </div>

      {!canFetch && <div className="sfe-alert">Select Fiscal Year, Period, and Division to load the dashboard.</div>}
      {loading && !summary && <div className="sfe-loading"><span />Loading KPI data…</div>}

      {canFetch && (
        <main className="sfe-content">
          {/* The HQ-performance response feeds the KPI cards, HQ Productivity, and the
              Critical HQ Watchlist, so its failure is announced once, here, above all
              three rather than repeated in each. */}
          <PanelError error={panelErrors.performance} testId="sfe-error-performance" />

          {loading && !hqData ? (
            <section className="sfe-section"><div className="sfe-section__head"><h2>KPI Cards</h2></div><div className="sfe-loading"><span />Loading</div></section>
          ) : (
          <div className="sfe-kpi-grid">
            <KpiCard title={`${periodLabel} SALES (FY ${fyLabel})`} value={fmtValue(summary?.total_sales)} subtitle={`Year: ${fyLabel}`}>
              <div className="sales-footer">
                <div className="sales-box sales-box-blue"><div className="sales-box-value">{fmtValue(summary?.total_budget)}</div><div className="sales-box-label">{periodLabel} TARGET</div></div>
                <div className="sales-box sales-box-green"><div className="sales-box-value">{fmtPct(summary?.achievement)}</div><div className="sales-box-label">ACHIEVEMENT</div></div>
              </div>
            </KpiCard>

            <KpiCard title={`GROWTH — ${periodLabel} YOY`} value={<span className={clsGrowth(summary?.growth)}>{fmtPct(summary?.growth, true)}</span>} subtitle={summary?.ly_sales ? `▲ ${periodLabel} ${fmtValue(summary.ly_sales)} → ${fmtValue(summary.total_sales)}` : "No prior year data"} toggle={<div className="kpi-hqtm-toggle"><button className={growthBasis === "HQ" ? "active" : ""} onClick={() => setGrowthBasis("HQ")}>HQ</button><button className={growthBasis === "TM" ? "active" : ""} onClick={() => setGrowthBasis("TM")}>TM</button></div>}>
              <div className="target-footer">
                {[{ band: "high", label: ">+10%", cls: "target-green", filter: h => h.growth > 10, count: growthBasis === "TM" ? tmGrowth.high : (gp.high || 0) },{ band: "grow", label: "0-10%", cls: "target-blue", filter: h => h.growth >= 0 && h.growth <= 10, count: growthBasis === "TM" ? tmGrowth.grow : (gp.grow || 0) },{ band: "slight", label: "-10-0%", cls: "target-orange", filter: h => h.growth >= -10 && h.growth < 0, count: growthBasis === "TM" ? tmGrowth.slight : (gp.slight || 0) },{ band: "sharp", label: "<-10%", cls: "target-red", filter: h => h.growth < -10, count: growthBasis === "TM" ? tmGrowth.sharp : (gp.sharp || 0) }].map(b => (<div key={b.band} className={`target-tile ${b.cls} clickable`} onClick={() => openDrill(`${periodLabel} · Growth ${b.label}`, b.filter, growthBasis)}><span className="tile-drill">⤢</span><div className="target-number">{b.count}</div><div className="target-label">{b.label}</div></div>))}
              </div>
              <div className="tm-slab-hint">Click a band to list {growthBasis === "TM" ? "TMs" : "HQs"}</div>
            </KpiCard>

            <KpiCard title="SPREAD OF PERFORMANCE" value={spreadBasis === "TM" ? `${totalTms} TM` : `${totalHqs} HQ`} subtitle={spreadBasis === "TM" ? `${totalHqs} HQ · TMs by ${periodLabel} achievement` : `${totalTms} TMs · unique HQ per division`} toggle={<div className="kpi-hqtm-toggle"><button className={spreadBasis === "HQ" ? "active" : ""} onClick={() => setSpreadBasis("HQ")}>HQ</button><button className={spreadBasis === "TM" ? "active" : ""} onClick={() => setSpreadBasis("TM")}>TM</button></div>}>
              <div className="target-footer">
                {[{ label: ">100%", cls: "target-green", filter: h => h.achievement > 100, count: spreadBasis === "TM" ? tmSpread.good : (sp.good || 0) },{ label: "95-100%", cls: "target-blue", filter: h => h.achievement >= 95 && h.achievement <= 100, count: spreadBasis === "TM" ? tmSpread.mid : (sp.mid || 0) },{ label: "90-95%", cls: "target-orange", filter: h => h.achievement >= 90 && h.achievement < 95, count: spreadBasis === "TM" ? tmSpread.warn : (sp.warn || 0) },{ label: "<90%", cls: "target-red", filter: h => h.achievement < 90 && (h.budget > 0 || h.hq_budget > 0), count: spreadBasis === "TM" ? tmSpread.bad : (sp.bad || 0) }].map((b, i) => (<div key={i} className={`target-tile ${b.cls} clickable`} onClick={() => openDrill(`${periodLabel} · Achievement ${b.label}`, b.filter, spreadBasis)}><span className="tile-drill">⤢</span><div className="target-number">{b.count}</div><div className="target-label">{b.label}</div></div>))}
              </div>
              <div className="tm-slab-hint">Click a band to list {spreadBasis === "TM" ? "TMs" : "HQs"}</div>
            </KpiCard>

            <KpiCard title={`PCPM — ${periodLabel}`} value={pcpmBasis === "TM" ? fmtValue(divPcpmTm) : fmtValue(divPcpmHq)} subtitle={`${pcpmBasis === "TM" ? "PER TM" : "PER HQ"} · Sales / ${selMonths.length || 1} mo · ${totalHqs} HQ / ${totalTms} TM`} toggle={<div className="kpi-hqtm-toggle"><button className={pcpmBasis === "HQ" ? "active" : ""} onClick={() => setPcpmBasis("HQ")}>HQ</button><button className={pcpmBasis === "TM" ? "active" : ""} onClick={() => setPcpmBasis("TM")}>TM</button></div>}>
              <div className="target-footer">
                {[{ label: ">150%", cls: "target-green", filter: h => h.vs_div_pcpm > 150, count: pcpmBasis === "TM" ? tmPcpm.high : (pp.high || 0) },{ label: "100-150%", cls: "target-blue", filter: h => h.vs_div_pcpm >= 100 && h.vs_div_pcpm <= 150, count: pcpmBasis === "TM" ? tmPcpm.above : (pp.above || 0) },{ label: "50-100%", cls: "target-orange", filter: h => h.vs_div_pcpm >= 50 && h.vs_div_pcpm < 100, count: pcpmBasis === "TM" ? tmPcpm.low : (pp.low || 0) },{ label: "<50%", cls: "target-red", filter: h => h.vs_div_pcpm < 50, count: pcpmBasis === "TM" ? tmPcpm.critical : (pp.critical || 0) }].map((b, i) => (<div key={i} className={`target-tile ${b.cls} clickable`} onClick={() => openDrill(`${periodLabel} · PCPM ${b.label}`, b.filter, pcpmBasis)}><span className="tile-drill">⤢</span><div className="target-number">{b.count}</div><div className="target-label">{b.label}</div></div>))}
              </div>
              <div className="tm-slab-hint">Click a band to list {pcpmBasis === "TM" ? "TMs" : "HQs"}</div>
            </KpiCard>
          </div>
          )}

          {/* Breakdown Table */}
          <section className="sfe-section">
            <div className="sfe-section__head">
              <h2>🎯 {periodLabel} — Target Achievement &amp; YoY Growth</h2>
              <div className="sfe-tabs sfe-tabs--pill">{BREAKDOWNS.map(dim => (<button key={dim} type="button" className={breakdown === dim ? "active" : ""} onClick={() => changeBreakdown(dim)}>{dim}</button>))}</div>
            </div>
            <div className="pa-summary">
              {[{ label: `${periodLabel} SALES`, value: fmtValue(summary?.total_sales), sub: fyLabel },{ label: `${periodLabel} TARGET`, value: fmtValue(summary?.total_budget), sub: "TGT" },{ label: "ACHIEVEMENT %", value: fmtPct(summary?.achievement), sub: `vs ${periodLabel} target` },{ label: "GROWTH % (YOY)", value: fmtPct(summary?.growth, true), sub: `${periodLabel} LY: ${fmtValue(summary?.ly_sales)}` }].map(c => (<div key={c.label} className="pa-summary-card"><div className="pa-summary-label">{c.label}</div><div className="pa-summary-value">{c.value}</div><div className="pa-summary-sub">{c.sub}</div></div>))}
            </div>
            {/* The banner replaces the table when the request failed and left no rows:
                "No data for current filters." would be an untrue explanation. */}
            <PanelError error={panelErrors.breakdown} testId="sfe-error-breakdown" />
            {breakdownLoading ? <div className="sfe-loading"><span />Loading...</div> : (panelErrors.breakdown && breakdownRows.length === 0) ? null : (
              <div className="sfe-table-wrap"><table className="sfe-table"><thead><tr><th>{breakdown.toUpperCase()}</th><th>{periodLabel} SALES (PRI)</th><th>{periodLabel} TARGET</th><th>ACH %</th><th>{periodLabel} LY</th><th>GROWTH %</th></tr></thead><tbody>{breakdownRows.length === 0 ? <tr><td colSpan={6} className="sfe-empty">No data for current filters.</td></tr> : breakdownRows.map((r, i) => (<tr key={(r.name || "") + i}><td>{r.name || "—"}</td><td>{fmtValue(r.total_sales)}</td><td>{r.total_budget > 0 ? fmtValue(r.total_budget) : "—"}</td><td><span className="ach-bar"><i className={`ach-bar-fill ach-${clsAch(r.achievement).replace("sfe-", "")}`} style={{ width: `${Math.min(100, Math.max(0, r.achievement || 0)) / 1.2}%` }} /></span><span className={clsAch(r.achievement)}>{fmtPct(r.achievement)}</span></td><td>{r.ly_sales > 0 ? fmtValue(r.ly_sales) : "—"}</td><td className={clsGrowth(r.growth)}>{fmtPct(r.growth, true)}</td></tr>))}</tbody></table></div>
            )}
          </section>

          {/* HQ Productivity */}
          {loading && !hqData ? (
            <section className="sfe-section"><div className="sfe-section__head"><h2>👥 HQ Productivity</h2></div><div className="sfe-loading"><span />Loading</div></section>
          ) : hqData && hqData.hq_list && hqData.hq_list.length > 0 && (() => {
            const sorted = [...hqData.hq_list].filter(h => h.pcpm_tm > 0).sort((a, b) => b.pcpm_tm - a.pcpm_tm);
            const topHqs = sorted.slice(0, 20);
            const bottomHqs = [...sorted].reverse().slice(0, 20);
            const pcpmRange = sorted.length > 0 ? `${fmtValue(sorted[sorted.length - 1].pcpm_tm)} – ${fmtValue(sorted[0].pcpm_tm)}` : "—";
            return (
              <section className="sfe-section">
                <div className="sfe-section__head"><h2>👥 {periodLabel} — HQ Productivity · Top 20 &amp; Bottom 20 by PCPM/TM</h2></div>
                <div className="hq-summary">
                  <div className="hq-summary-card hq-summary-blue"><div className="hq-summary-label">HQs in scope</div><div className="hq-summary-value">{totalHqs}</div><div className="hq-summary-sub">unique HQ per division</div></div>
                  <div className="hq-summary-card hq-summary-green"><div className="hq-summary-label">Division PCPM / HQ</div><div className="hq-summary-value">{fmtValue(divPcpmHq)}</div><div className="hq-summary-sub">{fmtValue(summary?.total_sales)} {periodLabel} sales</div></div>
                  <div className="hq-summary-card hq-summary-purple"><div className="hq-summary-label">Division PCPM / TM</div><div className="hq-summary-value">{fmtValue(divPcpmTm)}</div><div className="hq-summary-sub">{totalTms} TMs</div></div>
                  <div className="hq-summary-card hq-summary-amber"><div className="hq-summary-label">PCPM/TM Range</div><div className="hq-summary-value" style={{fontSize:"0.85em"}}>{pcpmRange}</div><div className="hq-summary-sub">bottom → top TM (staffed HQs)</div></div>
                </div>
                {[["▲ Top 20 — highest PCPM/TM", topHqs], ["▼ Bottom 20 — lowest PCPM/TM", bottomHqs]].map(([title, data]) => (
                  <div key={title} style={{marginBottom:"1.5rem"}}>
                    <h3 className="sfe-subtitle">{title}</h3>
                    <div className="sfe-table-wrap"><table className="sfe-table"><thead><tr><th>#</th><th>HQ</th><th>TMs</th><th>Sales</th><th>Target</th><th>Ach %</th><th>YoY %</th><th>PCPM/TM</th><th>vs Div</th></tr></thead><tbody>{data.map((h, i) => {
                      const pcpmCls = h.vs_div_pcpm > 100 ? "pcpm-good" : h.vs_div_pcpm >= 50 ? "pcpm-warn" : "pcpm-bad";
                      const budgetVal = h.budget > 0 ? fmtValue(h.budget) : "—";
                      const vsDivVal = h.vs_div_pcpm != null ? `${Math.round(h.vs_div_pcpm)}%` : "—";
                      return (<tr key={h.hq_name + i}><td>{i + 1}</td><td>{h.hq_name}</td><td>{h.tms}</td><td>{fmtValue(h.sales)}</td><td>{budgetVal}</td><td className={clsAch(h.achievement)}>{fmtPct(h.achievement)}</td><td className={clsGrowth(h.growth)}>{fmtPct(h.growth, true)}</td><td><span className={`pcpm-pill ${pcpmCls}`}>{fmtValue(h.pcpm_tm)}</span></td><td>{vsDivVal}</td></tr>);
                    })}</tbody></table></div>
                  </div>
                ))}
              </section>
            );
          })()}

          {/* Critical HQ Watchlist */}
          {hqData && hqData.hq_list && (() => {
            const critical = hqData.hq_list.filter(h => h.vs_div_pcpm != null && h.vs_div_pcpm < 50 && h.growth != null && h.growth < 0).sort((a, b) => (a.vs_div_pcpm || 0) - (b.vs_div_pcpm || 0));
            if (critical.length === 0) return (<section className="sfe-section"><div className="sfe-section__head"><h2>⚠️ Critical HQ Watchlist · {periodLabel}</h2></div><div className="sfe-empty" style={{color:"#22c55e", padding:"1.5rem"}}>✓ No HQs are both below 50% of division PCPM/TM and declining.</div></section>);
            const critSales = critical.reduce((s, h) => s + h.sales, 0);
            const critTarget = critical.reduce((s, h) => s + h.budget, 0);
            const critTms = critical.reduce((s, h) => s + h.tms, 0);
            return (
              <section className="sfe-section">
                <div className="sfe-section__head"><h2>⚠️ Critical HQ Watchlist · {periodLabel} — PCPM/TM &lt; 50% &amp; negative YoY</h2></div>
                <div className="crit-summary">
                  <div className="crit-summary-cell"><div className="crit-summary-label">No. of HQ</div><div className="crit-summary-value">{critical.length}</div></div>
                  <div className="crit-summary-cell"><div className="crit-summary-label">No. of TMs</div><div className="crit-summary-value">{critTms}</div></div>
                  <div className="crit-summary-cell"><div className="crit-summary-label">Target</div><div className="crit-summary-value">{fmtValue(critTarget)}</div></div>
                  <div className="crit-summary-cell"><div className="crit-summary-label">Sales</div><div className="crit-summary-value">{fmtValue(critSales)}</div></div>
                  <div className="crit-summary-cell"><div className="crit-summary-label">Ach %</div><div className="crit-summary-value">{critTarget > 0 ? fmtPct((critSales / critTarget) * 100) : "—"}</div></div>
                  <div className="crit-summary-cell"><div className="crit-summary-label">PCPM / TM</div><div className="crit-summary-value">{critTms > 0 ? fmtValue(critSales / critTms / Math.max(selMonths.length, 1)) : "—"}</div></div>
                </div>
                <div className="sfe-table-wrap"><table className="sfe-table"><thead><tr><th>#</th><th>HQ</th><th>TMs</th><th>Target</th><th>Sales</th><th>Ach %</th><th>PCPM/TM</th><th>vs Div</th><th>Growth %</th><th>Gap to Tgt</th></tr></thead><tbody>{critical.map((h, i) => {
                  const budgetDisplay = h.budget > 0 ? fmtValue(h.budget) : "—";
                  const vsDivDisplay = h.vs_div_pcpm != null ? `${Math.round(h.vs_div_pcpm)}%` : "—";
                  const gapDisplay = fmtValue(Math.max(h.budget - h.sales, 0));
                  return (<tr key={h.hq_name + i}><td>{i + 1}</td><td>{h.hq_name}</td><td>{h.tms}</td><td>{budgetDisplay}</td><td>{fmtValue(h.sales)}</td><td className="sfe-bad">{fmtPct(h.achievement)}</td><td><span className="pcpm-pill pcpm-bad">{fmtValue(h.pcpm_tm)}</span></td><td className="sfe-bad">{vsDivDisplay}</td><td className="sfe-bad">{fmtPct(h.growth, true)}</td><td>{gapDisplay}</td></tr>);
                })}</tbody></table></div>
              </section>
            );
          })()}

          {/* Volatility */}
          {/* A failed request leaves `volData` null, which used to make the whole
              section disappear silently; the banner takes its place. */}
          {volLoading ? <section className="sfe-section"><div className="sfe-loading"><span />Loading volatility…</div></section> : panelErrors.volatility ? (
            <section className="sfe-section">
              <div className="sfe-section__head"><h2>📊 Month-on-Month Volatility — HQs (±25%)</h2></div>
              <PanelError error={panelErrors.volatility} testId="sfe-error-volatility" />
            </section>
          ) : volData && (
            <section className="sfe-section">
              <div className="sfe-section__head"><h2>📊 Month-on-Month Volatility — HQs (±25%) {volData.prev_month && volData.curr_month ? `· ${volData.prev_month} → ${volData.curr_month}` : ""}</h2></div>
              {volData.rows.length === 0 ? <div className="sfe-empty" style={{color:"#22c55e", padding:"1.5rem"}}>✓ No HQs moved more than ±25% month-on-month.</div> : (
                <><div style={{padding:"0.5rem 1rem", fontSize:"0.85rem", color:"#64748b"}}><b>{volData.rows.length}</b> of {totalHqs} HQs moved &gt; ±25% · <span className="sfe-good">▲ {volData.rows.filter(r => r.change_pct > 0).length} up</span> · <span className="sfe-bad">▼ {volData.rows.filter(r => r.change_pct < 0).length} down</span></div>
                <div className="sfe-table-wrap"><table className="sfe-table"><thead><tr><th>#</th><th>HQ</th><th>Zone</th><th>{volData.prev_month || "Prev"}</th><th>{volData.curr_month || "Curr"}</th><th>Change</th><th>Change %</th></tr></thead><tbody>{volData.rows.map((r, i) => { const isUp = r.change_pct > 0; return (<tr key={r.hq_name + i}><td>{i + 1}</td><td>{r.hq_name}</td><td>{r.zone || "—"}</td><td>{fmtValue(r.prev_sales)}</td><td>{fmtValue(r.curr_sales)}</td><td className={isUp ? "sfe-good" : "sfe-bad"}>{isUp ? "+" : ""}{fmtValue(r.change_amount)}</td><td className={isUp ? "sfe-good" : "sfe-bad"}>{isUp ? "▲" : "▼"} {r.change_pct != null ? `${isUp ? "+" : ""}${r.change_pct}%` : "NEW"}</td></tr>); })}</tbody></table></div></>
              )}
            </section>
          )}

          {/* Credit Notes */}
          {/* The type tabs stay reachable on failure, so switching type is the retry. */}
          {cnLoading ? <section className="sfe-section"><div className="sfe-loading"><span />Loading credit notes…</div></section> : panelErrors.creditNotes ? (
            <section className="sfe-section">
              <div className="sfe-section__head">
                <h2>💧 Credit Notes Analysis — Sales Leakage</h2>
                <div className="sfe-tabs sfe-tabs--pill">{[{ key: "Expiry", label: "Expiry" },{ key: "Sales Return", label: "Sales Return" },{ key: "Rate Diff", label: "Rate Difference" }].map(t => (<button key={t.key} type="button" className={cnType === t.key ? "active" : ""} onClick={() => changeCnType(t.key)}>{t.label}</button>))}</div>
              </div>
              <PanelError error={panelErrors.creditNotes} testId="sfe-error-credit-notes" />
            </section>
          ) : cnData && (
            <section className="sfe-section">
              <div className="sfe-section__head">
                <h2>💧 Credit Notes Analysis — Sales Leakage</h2>
                <div className="sfe-tabs sfe-tabs--pill">{[{ key: "Expiry", label: "Expiry" },{ key: "Sales Return", label: "Sales Return" },{ key: "Rate Diff", label: "Rate Difference" }].map(t => (<button key={t.key} type="button" className={cnType === t.key ? "active" : ""} onClick={() => changeCnType(t.key)}>{t.label}</button>))}</div>
              </div>
              <div className="cn-cards">
                {(() => {
                  const totalSales = summary?.total_sales || 0;
                  const expiryPct = totalSales > 0 ? `${((cnData.totals?.expiry || 0) / totalSales * 100).toFixed(2)}%` : "—";
                  const returnPct = totalSales > 0 ? `${((cnData.totals?.sales_return || 0) / totalSales * 100).toFixed(2)}%` : "—";
                  const rateDiffPct = totalSales > 0 ? `${((cnData.totals?.rate_diff || 0) / totalSales * 100).toFixed(2)}%` : "—";
                  return (<>
                    <div className="cn-card cn-card--red"><div className="cn-card-label">Expiry</div><div className="cn-card-value">{fmtValue(cnData.totals?.expiry)}</div><div className="cn-card-pct">{expiryPct} of primary sales</div></div>
                    <div className="cn-card cn-card--rose"><div className="cn-card-label">Sales Return</div><div className="cn-card-value">{fmtValue(cnData.totals?.sales_return)}</div><div className="cn-card-pct">{returnPct} of primary sales</div></div>
                    <div className="cn-card cn-card--amber"><div className="cn-card-label">Rate Difference</div><div className="cn-card-value">{fmtValue(cnData.totals?.rate_diff)}</div><div className="cn-card-pct">{rateDiffPct} of primary sales</div></div>
                  </>);
                })()}
              </div>
              {cnData.rows.length === 0 ? <div className="sfe-empty">No {cnType.toLowerCase()} credit notes for this period/filter.</div> : (
                <div className="sfe-table-wrap"><table className="sfe-table"><thead><tr><th>#</th><th>HQ</th><th>Zone</th><th>Primary Sales</th><th>{cnType}</th><th>CN to Sales %</th></tr></thead><tbody>{cnData.rows.map((r, i) => { const cnCls = r.cn_pct > 2 ? "cn-pct-bad" : r.cn_pct > 1 ? "cn-pct-warn" : "cn-pct-good"; return (<tr key={r.hq_name + i}><td>{i + 1}</td><td>{r.hq_name}</td><td>{r.zone || "—"}</td><td>{fmtValue(r.primary_sales)}</td><td>{fmtValue(r.cn_amount)}</td><td><span className={`cn-pct-pill ${cnCls}`}>{r.cn_pct != null ? `${r.cn_pct}%` : "—"}</span></td></tr>); })}</tbody></table></div>
              )}
            </section>
          )}
        </main>
      )}

      {drillModal && <DrillModal title={drillModal.title} data={drillModal.data} mode={drillModal.mode} onClose={() => setDrillModal(null)} />}
    </div>
  );
}
